import React, { useState } from "react";
import { Flex, Button, Form, Text, FormInput } from "@fluentui/react-northstar";
import LocationsSettingsImage from "../../../assets/images/undraw_calling_kpbp.svg";
import Constants from "../../../constants/text-constants";
import RegExConst from "../../../constants/regex";
import ImageTitleContainer from "../../organisms/ImageTitleContainer";
import PropTypes from "prop-types";
import { useMst } from "../../../hooks/useMst";

const shouldDisableAddButton = (locationStore, originalLocation, locationCode, locationDescription, locationPhoneNumber, loading) => {
    return (locationStore.locations.some((loc) => loc.code === locationCode) && originalLocation.code !== locationCode) ||
        locationCode.length < 2 ||
        locationDescription.length === 0 ||
        locationPhoneNumber.length === 0 ||
        loading;
}

const LocationForm = (props) => {
    const {
        location,
        onCancel,
        onSave,
        title,
        subTitle,
        saveButtonText,
        disableCode,
        failureMessage
    } = props;
    const { locationStore } = useMst();
    const [locationPhoneNumber, setLocationPhoneNumber] = useState(location.phoneNumber);
    const [locationDescription, setLocationDescription] = useState(location.description);
    const [locationCode, setLocationCode] = useState(location.code);
    const [loading, setLoading] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [displayError, setDisplayError] = useState(false);
    const numberOnlyRegex = new RegExp(RegExConst.NUMBER_ONLY);

    const onSaveClicked = async () => {
        setDisplayError(false);
        setLoading(true);

        if (! await onSave({
            code: locationCode,
            description: locationDescription,
            phoneNumber: locationPhoneNumber,
        })) {
            setDisplayError(true);
            setLoading(false);
        }
    }

    const onLocationCodeChanged = (event) => {
        const value = event.target.value;
        // fluentui input fields don't seem to take a maxlength so doing it this way
        if (value.length > 2) return;

        if (numberOnlyRegex.test(value)) {
            setLocationCode(value);
            setDisplayError(false);
        }
        setCodeError(locationStore.locations.some((loc) => loc.code === value));
    };

    const onLocationDescriptionChanged = (event) => {
        const value = event.target.value;
        // fluentui input fields don't seem to take a maxlength so doing it this way
        if (value.length > 100) return;

        setLocationDescription(value);
        setDisplayError(false);
    };

    const onPhoneNumberChanged = (event) => {
        const value = event.target.value;
        if (value.length > 20) return;

        // allow numbers only
        if (numberOnlyRegex.test(value)) {
            setLocationPhoneNumber(value);
            setDisplayError(false);
        }
    };

    const disableAddButton = shouldDisableAddButton(
        locationStore,
        location,
        locationCode,
        locationDescription,
        locationPhoneNumber,
        loading
    );

    return (
        <ImageTitleContainer
            image={LocationsSettingsImage}
            title={title}
            subTitle={subTitle}
            testId="locationFormContainer"
        >
            <Flex className="tcm-form" gap="gap.large" fill column>
                <Form data-testid="locationForm">
                    <FormInput
                        data-testid="locationFormDescriptionInput"
                        fluid
                        label={Constants.LOCATION_NAME}
                        disabled={loading}
                        onChange={onLocationDescriptionChanged}
                        value={locationDescription}
                    />
                    <FormInput
                        data-testid="locationFormNumberInput"
                        fluid
                        label={Constants.PHONE_NUMBER}
                        disabled={loading}
                        onChange={onPhoneNumberChanged}
                        value={locationPhoneNumber}
                    />
                    <FormInput
                        data-testid="locationFormCodeInput"
                        fluid
                        label={Constants.CODE}
                        value={locationCode}
                        onChange={onLocationCodeChanged}
                        error={codeError}
                        errorMessage={codeError ? Constants.INVALID_LOCATION_CODE : ""}
                        disabled={disableCode || loading}
                    />
                    <Flex space="evenly" fill>
                        <Button
                            data-testid="locationFormCancelBtn"
                            content={Constants.CANCEL}
                            secondary
                            disabled={loading}
                            onClick={onCancel}
                        />
                        <Button
                            data-testid="locationFormAddLocationBtn"
                            content={saveButtonText}
                            primary
                            loading={loading}
                            disabled={disableAddButton}
                            onClick={onSaveClicked}
                        />
                    </Flex>
                </Form>
                {displayError ? <Text data-testid="locationFormError" align="center" error>{failureMessage}</Text> : null}
            </Flex>
        </ImageTitleContainer >
    );
}

LocationForm.propTypes = {
    location: PropTypes.object,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    failureMessage: PropTypes.string,
};

LocationForm.defaultProps = {
    location: {
        code: "",
        description: "",
        phoneNumber: "",
    },
    title: "",
    subTitle: "",
};

export default LocationForm;